import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../services/api';

export function useProfile() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) throw authError;
      if (!user) throw new Error('Not authenticated');

      // Try to fetch existing profile
      const { data, error: fetchError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (fetchError && fetchError.code === 'PGRST116') {
        // Profile doesn't exist — create one via upsert
        const newProfile = {
          user_id: user.id,
          full_name: user.user_metadata?.full_name || '',
          preferred_language: 'en',
          selected_llm_provider: 'anthropic',
          selected_llm_model: 'claude-sonnet-4-5-20250929',
          regulation_type: 'professional',
        };

        const { data: created, error: createError } = await supabase
          .from('user_profiles')
          .upsert(newProfile, { onConflict: 'user_id' })
          .select()
          .single();

        if (createError) throw createError;

        setProfile({ ...created, email: user.email });
      } else if (fetchError) {
        throw fetchError;
      } else {
        setProfile({ ...data, email: user.email });
      }
    } catch (err) {
      console.error('useProfile fetchProfile error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const updateProfile = useCallback(async (updates) => {
    try {
      setSaving(true);
      setError(null);

      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) throw authError;
      if (!user) throw new Error('Not authenticated');

      const { data, error: updateError } = await supabase
        .from('user_profiles')
        .update(updates)
        .eq('user_id', user.id)
        .select()
        .single();

      if (updateError) throw updateError;

      setProfile(prev => ({ ...prev, ...data, email: user.email }));
      return { success: true, data };
    } catch (err) {
      console.error('useProfile updateProfile error:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setSaving(false);
    }
  }, []);

  const uploadTeamLogo = useCallback(async (file) => {
    try {
      setSaving(true);
      setError(null);

      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) throw authError;
      if (!user) throw new Error('Not authenticated');

      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/team-logo-${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('team-logos')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true,
        });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from('team-logos')
        .getPublicUrl(fileName);

      const publicUrl = urlData.publicUrl;

      // Update profile with new logo URL
      const { data: updated, error: updateError } = await supabase
        .from('user_profiles')
        .update({ team_logo_url: publicUrl })
        .eq('user_id', user.id)
        .select()
        .single();

      if (updateError) throw updateError;

      setProfile(prev => ({ ...prev, ...updated, email: user.email }));
      return { success: true, url: publicUrl };
    } catch (err) {
      console.error('useProfile uploadTeamLogo error:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setSaving(false);
    }
  }, []);

  const testLLMConnection = useCallback(async (providerId, apiKey) => {
    try {
      if (!apiKey || apiKey.trim() === '') {
        return { success: false, message: 'API key is required.' };
      }

      if (providerId === 'anthropic') {
        const response = await fetch('https://api.anthropic.com/v1/messages', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': apiKey,
            'anthropic-version': '2023-06-01',
            'anthropic-dangerous-direct-browser-access': 'true',
          },
          body: JSON.stringify({
            model: 'claude-sonnet-4-5-20250929',
            max_tokens: 16,
            messages: [{ role: 'user', content: 'Say "connected" in one word.' }],
          }),
        });

        if (response.ok) {
          return { success: true, message: 'Anthropic API key verified successfully.' };
        } else {
          const errData = await response.json().catch(() => ({}));
          const errMsg = errData?.error?.message || `HTTP ${response.status}`;
          return { success: false, message: `Anthropic verification failed: ${errMsg}` };
        }
      }

      if (providerId === 'openai') {
        const response = await fetch('https://api.openai.com/v1/models', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${apiKey}`,
          },
        });

        if (response.ok) {
          return { success: true, message: 'OpenAI API key verified successfully.' };
        } else {
          const errData = await response.json().catch(() => ({}));
          const errMsg = errData?.error?.message || `HTTP ${response.status}`;
          return { success: false, message: `OpenAI verification failed: ${errMsg}` };
        }
      }

      // For all other providers, we accept the key but cannot verify
      return {
        success: true,
        message: 'Key saved. Verification available for Anthropic and OpenAI.',
      };
    } catch (err) {
      console.error('testLLMConnection error:', err);
      return { success: false, message: `Connection test failed: ${err.message}` };
    }
  }, []);

  const testStatsBombConnection = useCallback(async (username, token) => {
    try {
      if (!username || !token) {
        return { success: false, message: 'Username and token are required.' };
      }

      const credentials = btoa(`${username}:${token}`);
      const response = await fetch('https://data.statsbomb.com/api/v4/competitions', {
        method: 'GET',
        headers: {
          'Authorization': `Basic ${credentials}`,
        },
      });

      if (response.ok) {
        return { success: true, message: 'StatsBomb credentials verified successfully.' };
      } else {
        return { success: false, message: `StatsBomb verification failed: HTTP ${response.status}` };
      }
    } catch (err) {
      console.error('testStatsBombConnection error:', err);
      return { success: false, message: `StatsBomb connection test failed: ${err.message}` };
    }
  }, []);

  return {
    profile,
    loading,
    error,
    saving,
    updateProfile,
    uploadTeamLogo,
    testLLMConnection,
    testStatsBombConnection,
  };
}

export default useProfile;
